import React, { useState } from 'react';
import { Edit2, Loader2, Lock, Pill, Plus, Trash2, X } from 'lucide-react';
import { DeleteConfirmModal } from '../DeleteConfirmModal';
import type { FormularyItem, Prescription } from '../../types/clinical';

export interface PrescriptionSectionProps {
  prescriptions: Prescription[];
  isDoctor: boolean;
  // Form state
  showRxForm: boolean;
  setShowRxForm: (v: boolean) => void;
  rxDrug: string;
  setRxDrug: (v: string) => void;
  rxDose: string;
  setRxDose: (v: string) => void;
  rxFreq: string;
  setRxFreq: (v: string) => void;
  rxDuration: string;
  setRxDuration: (v: string) => void;
  rxQty: number;
  setRxQty: (v: number) => void;
  rxRepeats: number;
  setRxRepeats: (v: number) => void;
  formulary: FormularyItem[];
  // Inline edit state
  editingRxId: string | null;
  setEditingRxId: (id: string | null) => void;
  editingRxData: Partial<Prescription>;
  setEditingRxData: React.Dispatch<React.SetStateAction<Partial<Prescription>>>;
  editSaving: boolean;
  // PDF state
  pdfLoading: string | null;
  pdfError: string | null;
  setPdfError: (v: string | null) => void;
  // Handlers
  onCreatePrescription: () => void;
  onGeneratePdf: (rxId: string) => void;
  onSaveRxEdit: () => void;
  onDeletePrescription: (rxId: string) => Promise<void>;
}

export function PrescriptionSection({
  prescriptions, isDoctor,
  showRxForm, setShowRxForm,
  rxDrug, setRxDrug, rxDose, setRxDose,
  rxFreq, setRxFreq, rxDuration, setRxDuration,
  rxQty, setRxQty, rxRepeats, setRxRepeats,
  formulary,
  editingRxId, setEditingRxId, editingRxData, setEditingRxData, editSaving,
  pdfLoading, pdfError, setPdfError,
  onCreatePrescription, onGeneratePdf, onSaveRxEdit, onDeletePrescription,
}: PrescriptionSectionProps) {
  const [deleteRxId, setDeleteRxId] = useState<string | null>(null);
  const deleteRx = prescriptions.find(r => r.id === deleteRxId);

  return (
    <>
    {deleteRxId && deleteRx && (
      <DeleteConfirmModal
        title="Delete Prescription"
        description={`This will permanently delete the prescription for "${deleteRx.drugName} ${deleteRx.dose}". This action cannot be undone.`}
        onConfirm={async () => { await onDeletePrescription(deleteRxId); setDeleteRxId(null); }}
        onCancel={() => setDeleteRxId(null)}
      />
    )}
    <div className="mb-8">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wider flex items-center gap-2">
          <Pill className="h-4 w-4" /> Prescriptions ({prescriptions.length})
        </h2>
        {isDoctor && (
          <button onClick={() => setShowRxForm(!showRxForm)} className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1">
            <Plus className="h-3.5 w-3.5" /> New Prescription
          </button>
        )}
      </div>

      {showRxForm && (
        <div className="mb-4 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Drug</label>
              <input value={rxDrug} onChange={e => setRxDrug(e.target.value)} list="formulary-list" placeholder="Start typing..." className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" />
              <datalist id="formulary-list">
                {formulary.map(f => <option key={f.id} value={f.name}>{f.name} ({f.genericName})</option>)}
              </datalist>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Dose</label>
              <input value={rxDose} onChange={e => setRxDose(e.target.value)} placeholder="500mg" className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Frequency</label>
              <input value={rxFreq} onChange={e => setRxFreq(e.target.value)} placeholder="TDS" className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Duration</label>
              <input value={rxDuration} onChange={e => setRxDuration(e.target.value)} placeholder="7 days" className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Quantity</label>
              <input type="number" value={rxQty} onChange={e => setRxQty(Number(e.target.value))} min={1} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Repeats</label>
              <input type="number" value={rxRepeats} onChange={e => setRxRepeats(Number(e.target.value))} min={0} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" />
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={onCreatePrescription} disabled={!rxDrug} className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50">Save</button>
            <button onClick={() => setShowRxForm(false)} className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
          </div>
        </div>
      )}

      {pdfError && (
        <div className="mb-2 p-2 rounded bg-red-50 border border-red-200 text-red-700 text-xs flex items-center justify-between">
          <span>{pdfError}</span>
          <button onClick={() => setPdfError(null)} className="text-red-400 hover:text-red-600 ml-2"><X className="h-3 w-3" /></button>
        </div>
      )}
      {prescriptions.length === 0 ? (
        <p className="text-sm text-gray-500 py-2">No prescriptions.</p>
      ) : (
        <div className="space-y-2">
          {prescriptions.map(rx => (
            <div key={rx.id} className="bg-white rounded-lg shadow-sm border border-gray-200 px-4 py-3">
              {editingRxId === rx.id ? (
                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <div><label className="block text-xs text-gray-500 mb-1">Drug</label><input value={editingRxData.drugName || ''} onChange={e => setEditingRxData(d => ({ ...d, drugName: e.target.value }))} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" /></div>
                    <div><label className="block text-xs text-gray-500 mb-1">Dose</label><input value={editingRxData.dose || ''} onChange={e => setEditingRxData(d => ({ ...d, dose: e.target.value }))} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" /></div>
                    <div><label className="block text-xs text-gray-500 mb-1">Frequency</label><input value={editingRxData.frequency || ''} onChange={e => setEditingRxData(d => ({ ...d, frequency: e.target.value }))} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" /></div>
                    <div><label className="block text-xs text-gray-500 mb-1">Duration</label><input value={editingRxData.duration || ''} onChange={e => setEditingRxData(d => ({ ...d, duration: e.target.value }))} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" /></div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={onSaveRxEdit} disabled={editSaving} className="px-3 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50">{editSaving ? 'Saving...' : 'Save Changes'}</button>
                    <button onClick={() => setEditingRxId(null)} className="px-3 py-1 text-xs text-gray-600">Cancel</button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm font-medium text-gray-900">{rx.drugName} {rx.dose}</span>
                    <span className="text-sm text-gray-500 ml-2">{rx.frequency} for {rx.duration}</span>
                    {rx.dateIssued && (
                      <span className="text-xs text-gray-400 ml-2">
                        {new Date(rx.dateIssued).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </span>
                    )}
                    <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${rx.status === 'approved' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                      {rx.status}
                    </span>
                    {rx.pdfGcsPath && <span className="ml-1 text-xs text-gray-400"><Lock className="h-3 w-3 inline" /></span>}
                  </div>
                  {isDoctor && (
                    <div className="flex items-center gap-2">
                      {!rx.pdfGcsPath && (
                        <button onClick={() => { setEditingRxId(rx.id); setEditingRxData({ drugName: rx.drugName, dose: rx.dose, frequency: rx.frequency, duration: rx.duration, quantity: rx.quantity, repeats: rx.repeats }); }} className="p-1 text-gray-400 hover:text-blue-600" title="Edit"><Edit2 className="h-3.5 w-3.5" /></button>
                      )}
                      <button onClick={() => onGeneratePdf(rx.id)} disabled={pdfLoading === rx.id} className="text-xs text-blue-600 hover:text-blue-800 disabled:opacity-50 flex items-center gap-1">
                        {pdfLoading === rx.id ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                        PDF
                      </button>
                      <button onClick={() => setDeleteRxId(rx.id)} className="p-1 text-gray-400 hover:text-red-600" title="Delete"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
    </>
  );
}
